import { execFile } from 'node:child_process';
import { promisify } from 'node:util';
import type {
  Gap,
  GapCategory,
  GapSeverity,
  ModelStationOutput,
  ProductionChecklist,
  ProductionMapCategoryKey,
  ScanResult,
} from '../../../src/station/types';

const execFileAsync = promisify(execFile);
import {
  buildVercelSupabaseAudit,
  collectVercelSupabaseAuditInput,
  type VercelSupabaseAuditResult,
} from './commands/audit';

/**
 * Deterministic local station engine.
 *
 * Replaces the managed model call: synthesizes a `ModelStationOutput`
 * (score, gaps, summary, archetype) purely from local repo evidence —
 * stack signals from the deep scan plus the offline Vercel/Supabase audit.
 * Every emitted gap that has a heal recipe uses the canonical recipe gapId
 * so `viberaven --heal --apply --gap <id>` works out of the box.
 */

type Signals = ScanResult['stackSignals'];

export type GapSeed = {
  id: string;
  category: GapCategory;
  severity: GapSeverity;
  title: string;
  detail: string;
  copyPrompt: string;
  primaryMapCategory: ProductionMapCategoryKey;
};

function hasFilePath(scan: ScanResult, pattern: RegExp): boolean {
  return scan.files.some((f) => pattern.test(f.path));
}

function fileContentMatches(scan: ScanResult, pathPattern: RegExp, contentPattern: RegExp): boolean {
  return scan.files.some(
    (f) => pathPattern.test(f.path) && typeof f.content === 'string' && contentPattern.test(f.content)
  );
}

function hasApiRoutes(scan: ScanResult): boolean {
  return hasFilePath(scan, /(^|\/)(app|pages|src\/app|src\/pages)\/api\//) ||
    hasFilePath(scan, /(^|\/)app\/.*\/route\.(ts|js|tsx|jsx)$/) ||
    hasFilePath(scan, /(^|\/)(server|api)\//);
}

function detectStack(signals: Signals, scan: ScanResult): string[] {
  const stack: string[] = [];
  if (signals.hasNextJs) stack.push('Next.js');
  if (signals.hasVite) stack.push('Vite');
  if (signals.hasSupabase) stack.push('Supabase');
  if (signals.hasNeon) stack.push('Neon');
  if (signals.hasPrisma) stack.push('Prisma');
  if (signals.hasDrizzle) stack.push('Drizzle');
  if (signals.hasMongoDb || signals.hasMongoose) stack.push('MongoDB');
  if (signals.hasClerk) stack.push('Clerk');
  if (signals.hasAuthJs) stack.push('Auth.js');
  if (signals.hasStripe) stack.push('Stripe');
  if (signals.hasPolar) stack.push('Polar');
  if (signals.hasPaddle) stack.push('Paddle');
  if (signals.hasVercel || hasFilePath(scan, /(^|\/)vercel\.json$/)) stack.push('Vercel');
  if (signals.hasSentry) stack.push('Sentry');
  if (signals.hasPostHog) stack.push('PostHog');
  if (signals.hasUpstash) stack.push('Upstash');
  if (signals.hasDocker) stack.push('Docker');
  return stack;
}

function detectArchetype(signals: Signals, stack: string[]): string {
  const hasPayments = Boolean(signals.hasStripe || signals.hasPolar || signals.hasPaddle);
  const hasAuth = Boolean(signals.hasAuth || signals.hasClerk || signals.hasAuthJs || signals.hasSupabase);
  if (hasPayments && hasAuth) return 'SaaS product';
  if (hasAuth && signals.hasSupabase) return 'Full-stack app';
  if (signals.hasLanding && !hasAuth) return 'Landing page';
  if (stack.length === 0) return 'unknown';
  return 'Web app';
}

const ENV_DRIFT_IGNORE = new Set([
  'NODE_ENV', 'CI', 'PORT', 'TZ', 'ANALYZE',
  'VERCEL', 'VERCEL_ENV', 'VERCEL_URL', 'VERCEL_REGION',
  'NEXT_RUNTIME', 'NEXT_PHASE',
]);

/** Env vars read via `process.env.X` in code but not documented in `.env.example`. */
export function collectEnvVarDrift(scan: ScanResult): string[] {
  const example = scan.files.find(
    (file) => /(^|\/)\.env\.example$/.test(file.path) && typeof file.content === 'string'
  );
  if (!example || typeof example.content !== 'string') return [];

  const declared = new Set(
    example.content
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => line.length > 0 && !line.startsWith('#') && line.includes('='))
      .map((line) => line.split('=')[0].trim())
  );

  const used = new Set<string>();
  const pattern = /process\.env\.([A-Z][A-Z0-9_]{2,})/g;
  for (const file of scan.files) {
    if (!/\.(ts|tsx|js|jsx|mjs|cjs)$/.test(file.path) || typeof file.content !== 'string') continue;
    let match: RegExpExecArray | null;
    while ((match = pattern.exec(file.content)) !== null) {
      used.add(match[1]);
    }
  }

  return [...used]
    .filter((name) => !declared.has(name) && !ENV_DRIFT_IGNORE.has(name) && !name.startsWith('npm_') && !name.startsWith('VERCEL_'))
    .sort();
}

/** Files with Supabase selects or Prisma findMany calls that never bound the result set. */
export function findUnboundedQueryFiles(scan: ScanResult): string[] {
  const flagged: string[] = [];
  for (const file of scan.files) {
    if (!/\.(ts|tsx|js|jsx)$/.test(file.path) || typeof file.content !== 'string') continue;
    const content = file.content;
    const supabaseUnbounded =
      /\.from\(\s*['"`][^'"`]+['"`]\s*\)[\s\S]{0,120}?\.select\(/.test(content) &&
      !/\.(limit|range|single|maybeSingle)\(/.test(content);
    const prismaUnbounded = /\.findMany\(/.test(content) && !/\btake\s*:/.test(content);
    if (supabaseUnbounded || prismaUnbounded) flagged.push(file.path);
  }
  return flagged.sort();
}

/** Next.js files rendering raw `<img>` without next/image. */
export function findRawImageFiles(scan: ScanResult): string[] {
  const flagged: string[] = [];
  for (const file of scan.files) {
    if (!/\.(tsx|jsx)$/.test(file.path) || typeof file.content !== 'string') continue;
    if (/<img[\s>]/.test(file.content) && !file.content.includes('next/image')) {
      flagged.push(file.path);
    }
  }
  return flagged.sort();
}

export function noReleaseTagsSeed(commitCount: number): GapSeed {
  return {
    id: 'no_release_tags',
    category: 'DEPLOYMENT',
    severity: 'info',
    title: 'No release tags for version context',
    detail: `This repo has ${commitCount} commits and zero git tags. Release diffs, "which version broke it", and rollback all need anchors — right now there is no known-good version to compare against.`,
    copyPrompt: 'Tag the current known-good state (`git tag v0.1.0 && git push --tags`) and tag every deploy from now on, so release diffs and rollback have real anchors.',
    primaryMapCategory: 'deployment',
  };
}

async function detectReleaseTagGap(workspacePath: string): Promise<GapSeed | null> {
  try {
    const [tags, count] = await Promise.all([
      execFileAsync('git', ['tag', '--list'], { cwd: workspacePath }),
      execFileAsync('git', ['rev-list', '--count', 'HEAD'], { cwd: workspacePath }),
    ]);
    const tagCount = tags.stdout.split('\n').filter((line) => line.trim().length > 0).length;
    const commitCount = Number.parseInt(count.stdout.trim(), 10) || 0;
    if (tagCount === 0 && commitCount >= 20) {
      return noReleaseTagsSeed(commitCount);
    }
    return null;
  } catch {
    return null;
  }
}

export function buildGapSeeds(scan: ScanResult, audit: VercelSupabaseAuditResult): GapSeed[] {
  const signals = scan.stackSignals;
  const seeds: GapSeed[] = [];
  const isNext = Boolean(signals.hasNextJs);
  const hasAuthLayer = Boolean(signals.hasAuth || signals.hasAuthJs || signals.hasClerk);
  const hasDatabase = Boolean(
    signals.hasSupabase || signals.hasPrisma || signals.hasDrizzle || signals.hasMongoose ||
    signals.hasNeon || signals.hasMongoDb || signals.hasPlanetScale || signals.hasTurso
  );
  const apiRoutes = hasApiRoutes(scan);

  // Committed secrets — highest priority, no auto recipe.
  if (scan.secretsFound.length > 0) {
    seeds.push({
      id: 'secrets_in_repo',
      category: 'SECURITY & AUTH',
      severity: 'critical',
      title: 'Secret files are not gitignored',
      detail: `Files that normally hold live credentials are not covered by .gitignore (${scan.secretsFound.slice(0, 5).join(', ')}). One \`git add .\` away from leaking keys into history.`,
      copyPrompt: 'Add the listed secret files (.env and friends) to .gitignore, verify none of them are already tracked with `git ls-files`, and if any were ever committed, rotate those credentials with the provider and scrub git history.',
      primaryMapCategory: 'security',
    });
  }

  // Offline Vercel/Supabase audit findings.
  for (const check of audit.checks) {
    if (check.status !== 'needs_work') continue;
    const evidence = check.evidence.length > 0 ? `\nEvidence:\n${check.evidence.map((e) => `- ${e}`).join('\n')}` : '';
    if (check.id === 'supabase-rls-policy-proof') {
      seeds.push({
        id: 'rls_disabled',
        category: 'DATABASE & DATA',
        severity: 'critical',
        title: 'No RLS policy proof in migrations',
        detail: `${check.summary}${evidence}`,
        copyPrompt: 'Add a Supabase migration that enables row level security and creates explicit policies for every user-facing table (ALTER TABLE ... ENABLE ROW LEVEL SECURITY; CREATE POLICY ...). Keep the SQL in supabase/migrations so the repo carries the proof.',
        primaryMapCategory: 'database',
      });
    } else if (check.id === 'supabase-service-role-boundary') {
      seeds.push({
        id: 'service_role_boundary',
        category: 'SECURITY & AUTH',
        severity: 'critical',
        title: 'Service-role key may reach client code',
        detail: `${check.summary}${evidence}`,
        copyPrompt: 'Audit every use of SUPABASE_SERVICE_ROLE_KEY in this repo. It must only be read in server-only modules (route handlers, server actions, edge functions) — never in client components or files bundled for the browser. Move violations behind a server boundary.',
        primaryMapCategory: 'security',
      });
    } else if (check.id === 'vercel-supabase-pooler-port') {
      seeds.push({
        id: 'pooler_port_mismatch',
        category: 'DATABASE & DATA',
        severity: 'warning',
        title: 'Supabase connection is not using the pooler port',
        detail: `${check.summary}${evidence}`,
        copyPrompt: 'Update the DATABASE_URL used on Vercel to Supabase\'s transaction pooler (port 6543, pgbouncer) instead of the direct 5432 connection so serverless functions do not exhaust database connections.',
        primaryMapCategory: 'database',
      });
    }
  }

  // Auth secret evidence.
  if (signals.hasAuthJs) {
    const secretDeclared =
      fileContentMatches(scan, /\.env(\.example|\.local)?$/, /(AUTH_SECRET|NEXTAUTH_SECRET)/) ||
      fileContentMatches(scan, /\.(ts|js|mjs|tsx)$/, /(AUTH_SECRET|NEXTAUTH_SECRET)/);
    if (!secretDeclared) {
      seeds.push({
        id: 'auth_secret_missing',
        category: 'SECURITY & AUTH',
        severity: 'critical',
        title: 'Auth secret is not configured',
        detail: 'Auth.js/NextAuth is installed but no AUTH_SECRET or NEXTAUTH_SECRET is referenced anywhere in the repo. Sessions will be signed with an unstable or missing secret.',
        copyPrompt: 'Add AUTH_SECRET to .env.example (with a placeholder), generate a real value with `openssl rand -base64 32`, set it in the deployment environment, and make sure the auth config reads it.',
        primaryMapCategory: 'auth',
      });
    }
  }

  // DATABASE_URL evidence for ORM stacks.
  if ((signals.hasPrisma || signals.hasDrizzle) && signals.hasEnvExample) {
    const declared = fileContentMatches(scan, /\.env\.example$/, /DATABASE_URL/);
    if (!declared) {
      seeds.push({
        id: 'database_url_missing',
        category: 'DATABASE & DATA',
        severity: 'warning',
        title: 'DATABASE_URL missing from .env.example',
        detail: 'An ORM is installed but .env.example does not document DATABASE_URL, so fresh clones and CI cannot connect to the database.',
        copyPrompt: 'Add DATABASE_URL=postgres://user:password@host:6543/db (placeholder only, never a real value) to .env.example and document where the real value lives.',
        primaryMapCategory: 'database',
      });
    }
  }

  // Rate limiting on API surface.
  if (apiRoutes && !signals.hasRateLimit) {
    seeds.push({
      id: 'missing_rate_limit',
      category: 'SECURITY & AUTH',
      severity: 'warning',
      title: 'API routes have no rate limiting',
      detail: 'The repo exposes API routes but no rate limiting library or middleware was detected. One abusive client can exhaust quotas or run up provider bills.',
      copyPrompt: 'Add rate limiting to the API routes (for example @upstash/ratelimit with Redis, or an in-memory limiter in middleware for a single-region deploy). Protect auth, webhook, and any AI/LLM endpoints first.',
      primaryMapCategory: 'security',
    });
  }

  // CSP header.
  if (isNext) {
    const hasCsp =
      fileContentMatches(scan, /next\.config\.(js|mjs|ts)$/, /Content-Security-Policy/i) ||
      fileContentMatches(scan, /middleware\.(ts|js)$/, /Content-Security-Policy/i) ||
      fileContentMatches(scan, /vercel\.json$/, /Content-Security-Policy/i);
    if (!hasCsp) {
      seeds.push({
        id: 'missing_csp_header',
        category: 'SECURITY & AUTH',
        severity: 'warning',
        title: 'No Content-Security-Policy header',
        detail: 'No CSP header was found in next.config, middleware, or vercel.json. XSS on any page can reach every user session.',
        copyPrompt: 'Add a Content-Security-Policy header (start with a report-only policy) via next.config headers() or middleware, then tighten script-src and connect-src for the providers this app actually uses.',
        primaryMapCategory: 'security',
      });
    }
  }

  // Error boundary.
  if (isNext && !signals.hasErrorBoundary) {
    seeds.push({
      id: 'missing_error_boundary',
      category: 'ERROR HANDLING',
      severity: 'warning',
      title: 'No error boundary',
      detail: 'No error.tsx / ErrorBoundary component was detected. A single render error white-screens the whole app for real users.',
      copyPrompt: 'Add an app/error.tsx (and app/global-error.tsx) error boundary that shows a friendly retry UI and reports the error to the monitoring tool.',
      primaryMapCategory: 'errorHandling',
    });
  }

  // Health route.
  if (apiRoutes && !hasFilePath(scan, /(health|healthz|ping)\/?(route)?\.(ts|js|tsx|jsx)$/i)) {
    seeds.push({
      id: 'missing_health_route',
      category: 'DEPLOYMENT',
      severity: 'info',
      title: 'No health check endpoint',
      detail: 'No /api/health route was found. Uptime monitors and deploy checks have nothing cheap to probe.',
      copyPrompt: 'Add a GET /api/health route that returns { ok: true } plus a timestamp, and point the uptime monitor at it.',
      primaryMapCategory: 'deployment',
    });
  }

  // Loading states.
  if (isNext && !signals.hasLoadingStates) {
    seeds.push({
      id: 'missing_loading_state',
      category: 'MISSING FEATURES',
      severity: 'info',
      title: 'No loading states',
      detail: 'No loading.tsx or skeleton components were detected. Slow data fetches will look like a frozen app.',
      copyPrompt: 'Add loading.tsx files (or skeleton components) for the main data-driven routes so navigation shows immediate feedback.',
      primaryMapCategory: 'frontend',
    });
  }

  // 404 page.
  if (isNext && !hasFilePath(scan, /(not-found|404)\.(tsx|jsx|ts|js)$/)) {
    seeds.push({
      id: 'missing_404_page',
      category: 'MISSING FEATURES',
      severity: 'info',
      title: 'No custom 404 page',
      detail: 'No not-found.tsx / 404 page was detected. Broken links drop users on an unstyled framework page.',
      copyPrompt: 'Add an app/not-found.tsx page with the product\'s branding and a link back to the main flow.',
      primaryMapCategory: 'frontend',
    });
  }

  // Payments webhook evidence.
  if (signals.hasStripe) {
    const webhookRoute = hasFilePath(scan, /webhook/i) ||
      fileContentMatches(scan, /\.(ts|js|tsx)$/, /stripe.*webhook|constructEvent/i);
    if (!webhookRoute) {
      seeds.push({
        id: 'missing_stripe_webhook',
        category: 'MISSING FEATURES',
        severity: 'warning',
        title: 'Stripe installed but no webhook handler',
        detail: 'Stripe is a dependency but no webhook route or signature verification (constructEvent) was found. Payment state will silently drift from app state.',
        copyPrompt: 'Add a Stripe webhook route (e.g. app/api/webhooks/stripe/route.ts) that verifies the signature with stripe.webhooks.constructEvent and handles checkout.session.completed plus subscription lifecycle events.',
        primaryMapCategory: 'payments',
      });
    }
  }

  // Env contract drift — vars read in code but missing from .env.example.
  if (signals.hasEnvExample) {
    const drifted = collectEnvVarDrift(scan);
    if (drifted.length > 0) {
      const shown = drifted.slice(0, 5).join(', ');
      seeds.push({
        id: 'env_var_drift',
        category: 'DEPLOYMENT',
        severity: 'warning',
        title: 'Env vars used in code but missing from .env.example',
        detail: `Code reads ${drifted.length} environment variable${drifted.length === 1 ? '' : 's'} that .env.example does not document (${shown}${drifted.length > 5 ? ', …' : ''}). Fresh clones, CI, and the production deploy can silently run without them.`,
        copyPrompt: `Add placeholder entries for these variables to .env.example (never real values): ${shown}${drifted.length > 5 ? ' and the rest' : ''}. Then confirm each one is set in the deployment environment (Vercel project settings).`,
        primaryMapCategory: 'deployment',
      });
    }
  }

  // Unbounded query surface — scaling risk.
  if (hasDatabase) {
    const unbounded = findUnboundedQueryFiles(scan);
    if (unbounded.length > 0) {
      const shown = unbounded.slice(0, 3).join(', ');
      seeds.push({
        id: 'unbounded_query',
        category: 'PERFORMANCE',
        severity: 'warning',
        title: 'Database queries with no row limit',
        detail: `${unbounded.length} file${unbounded.length === 1 ? '' : 's'} run Supabase selects or Prisma findMany with no .limit()/.range()/take bound (${shown}${unbounded.length > 3 ? ', …' : ''}). These work with 50 test rows and melt with 50,000 production rows.`,
        copyPrompt: 'Add explicit pagination to every unbounded query: Supabase `.range(from, to)` or `.limit(n)`, Prisma `take`/`skip` (or cursor pagination). Start with the files listed in the evidence.',
        primaryMapCategory: 'database',
      });
    }
  }

  // Raw <img> in a Next.js app — bandwidth/LCP at scale.
  if (isNext) {
    const rawImages = findRawImageFiles(scan);
    if (rawImages.length > 0) {
      seeds.push({
        id: 'missing_image_optimization',
        category: 'PERFORMANCE',
        severity: 'info',
        title: 'Raw <img> tags bypass image optimization',
        detail: `${rawImages.length} component${rawImages.length === 1 ? '' : 's'} render raw <img> tags instead of next/image (${rawImages.slice(0, 3).join(', ')}${rawImages.length > 3 ? ', …' : ''}). Full-size images ship to every device, hurting LCP and bandwidth as traffic grows.`,
        copyPrompt: 'Replace raw <img> tags with next/image in the listed components (or add width/height + loading="lazy" where next/image does not fit, e.g. external unoptimizable sources).',
        primaryMapCategory: 'frontend',
      });
    }
  }

  // Monitoring.
  if (!signals.hasSentry && !signals.hasPostHog && (hasAuthLayer || hasDatabase)) {
    seeds.push({
      id: 'missing_monitoring',
      category: 'DEPLOYMENT',
      severity: 'info',
      title: 'No error monitoring detected',
      detail: 'No Sentry/PostHog (or similar) instrumentation was found. Production errors will only surface when users complain.',
      copyPrompt: 'Add error monitoring (Sentry is the common default for Next.js) so unhandled exceptions in production are captured with stack traces.',
      primaryMapCategory: 'monitoring',
    });
  }

  return seeds;
}

function scoreFrom(gaps: GapSeed[]): number {
  let score = 96;
  for (const gap of gaps) {
    if (gap.severity === 'critical') score -= 18;
    else if (gap.severity === 'warning') score -= 8;
    else score -= 3;
  }
  return Math.max(5, Math.min(96, score));
}

function labelFor(score: number, criticalCount: number): string {
  if (criticalCount > 0) return 'Blocked';
  if (score >= 85) return 'Launch ready';
  if (score >= 60) return 'Almost there';
  return 'Needs work';
}

function checklistFrom(gaps: GapSeed[]): ProductionChecklist {
  const base: ProductionChecklist = {
    security: 90, database: 90, auth: 90, errorHandling: 90,
    deployment: 90, testing: 70, landing: 80, monitoring: 80,
  };
  const areaFor: Partial<Record<ProductionMapCategoryKey, keyof ProductionChecklist>> = {
    security: 'security', auth: 'auth', database: 'database',
    errorHandling: 'errorHandling', deployment: 'deployment',
    monitoring: 'monitoring', landing: 'landing', testing: 'testing',
  };
  for (const gap of gaps) {
    const area = areaFor[gap.primaryMapCategory];
    if (!area) continue;
    const penalty = gap.severity === 'critical' ? 40 : gap.severity === 'warning' ? 20 : 8;
    base[area] = Math.max(5, base[area] - penalty);
  }
  return base;
}

export type LocalStationInput = {
  scan: ScanResult;
  workspacePath: string;
};

export async function buildLocalStationOutput(input: LocalStationInput): Promise<Partial<ModelStationOutput>> {
  const { scan, workspacePath } = input;
  const auditInput = await collectVercelSupabaseAuditInput(workspacePath);
  const audit = buildVercelSupabaseAudit(auditInput);

  const seeds = buildGapSeeds(scan, audit);
  const releaseTagSeed = await detectReleaseTagGap(workspacePath);
  if (releaseTagSeed) {
    seeds.push(releaseTagSeed);
  }
  const stackDetected = detectStack(scan.stackSignals, scan);
  const archetype = detectArchetype(scan.stackSignals, stackDetected);
  const criticalCount = seeds.filter((g) => g.severity === 'critical').length;
  const warningCount = seeds.filter((g) => g.severity === 'warning').length;
  const score = scoreFrom(seeds);

  const summary =
    seeds.length === 0
      ? `Local scan found no launch blockers across ${scan.totalFilesScanned} files. Provider dashboard checks (billing, DNS, live webhooks) still need human eyes.`
      : `Local scan of ${scan.totalFilesScanned} files found ${criticalCount} blocker${criticalCount === 1 ? '' : 's'} and ${warningCount} warning${warningCount === 1 ? '' : 's'}. Everything below was detected from repo evidence — no cloud, no login.`;

  const gaps: Gap[] = seeds.map((seed) => ({
    id: seed.id,
    category: seed.category,
    severity: seed.severity,
    title: seed.title,
    detail: seed.detail,
    copyPrompt: seed.copyPrompt,
    toolSuggestions: [],
    mcpSuggestion: null,
    primaryMapCategory: seed.primaryMapCategory,
    affectedMapCategories: [seed.primaryMapCategory],
  }));

  return {
    score,
    scoreLabel: labelFor(score, criticalCount),
    summary,
    archetype,
    gaps,
    stackDetected,
    missingLayers: [],
    quickWins: seeds.filter((s) => s.severity === 'info').map((s) => s.title),
    productionChecklist: checklistFrom(seeds),
  };
}
